"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { ShoppingCart, Tv, ArrowUpRight } from "lucide-react";

const PAIRS = [
  {
    slug: "online-ordering",
    icon: ShoppingCart,
    label: { en: "Online Ordering — 0% Fee", es: "Pedidos Online — 0% Fee" },
    desc: {
      en: "The website is where they order. Direct, no marketplace commission. Your customer list stays yours.",
      es: "El sitio es donde ordenan. Directo, sin comisión de marketplace. Tu lista de clientes es tuya.",
    },
  },
  {
    slug: "live-menu-boards",
    icon: Tv,
    label: { en: "Live Menu Boards", es: "Menu Boards en Vivo" },
    desc: {
      en: "Same menu source. Edit once. Live on website + every TV + every screen.",
      es: "Mismo source de menú. Editas una vez. Vivo en sitio + cada TV + cada pantalla.",
    },
  },
];

export default function WebsitePairs() {
  const { t } = useLang();

  return (
    <section className="relative py-24 px-6 bg-white overflow-hidden">
      <div className="max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="text-[11px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
            {t("Pairs well with", "Combina bien con")}
          </div>
          <h2 className="text-2xl md:text-[36px] font-black tracking-tight text-redy-charcoal">
            {t("One menu. Every surface.", "Un menú. Cada superficie.")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PAIRS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
              >
                <Link
                  href={`/features/${p.slug}`}
                  className="group block bg-[#FAFAF8] rounded-[24px] p-7 border border-black/5 hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="w-12 h-12 rounded-[14px] flex items-center justify-center group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: "rgba(244,114,43,0.12)" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: "#F4722B" }} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-redy-secondary/40 group-hover:text-redy-charcoal group-hover:rotate-12 transition-all" />
                  </div>
                  <div className="text-[18px] font-black text-redy-charcoal mb-2">
                    {t(p.label.en, p.label.es)}
                  </div>
                  <div className="text-[14px] font-medium text-redy-secondary leading-relaxed">
                    {t(p.desc.en, p.desc.es)}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
